import React, { useState } from "react";
import { Target, Eye, ShieldCheck, Sparkles, Users, MapPin, Mail, Phone, Loader2, CheckCircle2 } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { LeadForm } from "../components/LeadForm";
import { submitContact } from "../lib/api";

export const AboutPage = () => (
  <div data-testid="about-page">
    <section className="bg-gradient-to-br from-[#EAF5FD] to-white">
      <div className="ol-container py-10 lg:py-16">
        <Breadcrumbs items={[{ label: "About Us" }]}/>
        <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold font-heading text-[#273B59] leading-[1.05]">India's <span className="ol-gradient-text">trusted</span><br/>loan marketplace.</h1>
        <p className="mt-5 text-lg text-[#475569] max-w-2xl">OneLoan.in is a digital loan distribution platform helping Indians compare, evaluate, and apply for Personal, Business, and Flexi Overdraft loans from 16+ leading Banks and NBFCs.</p>
      </div>
    </section>

    <section className="ol-section">
      <div className="ol-container grid md:grid-cols-2 gap-8">
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-8">
          <Target className="text-[#1088D4]" size={28}/>
          <h2 className="mt-4 text-2xl font-bold font-heading text-[#273B59]">Our Mission</h2>
          <p className="mt-3 text-[#475569] leading-relaxed">To make borrowing simple, transparent, and accessible for every Indian — from first-time salaried borrowers to growing MSMEs and established enterprises.</p>
        </div>
        <div className="bg-[#273B59] text-white rounded-2xl p-8">
          <Eye className="text-[#1088D4]" size={28}/>
          <h2 className="mt-4 text-2xl font-bold font-heading">Our Vision</h2>
          <p className="mt-3 text-white/80 leading-relaxed">To become India's most trusted digital lending marketplace by combining technology, transparency, and human-led expert advisory.</p>
        </div>
      </div>
    </section>

    <section className="bg-[#F7F9FC] ol-section">
      <div className="ol-container">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.2em] text-[#1088D4] font-semibold">Why OneLoan</p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold font-heading text-[#273B59]">Built on three core values</h2>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {[
            { icon: ShieldCheck, title: "Transparency", desc: "All rates, charges and terms are disclosed upfront. No hidden fees, ever." },
            { icon: Sparkles, title: "Speed", desc: "From application to disbursal in 24-72 hours for eligible applicants." },
            { icon: Users, title: "Human Advisory", desc: "Every application is supported by a dedicated, India-trained loan advisor." },
          ].map(v => (
            <div key={v.title} className="bg-white p-6 rounded-xl border border-[#E2E8F0]">
              <v.icon className="text-[#1088D4]" size={24}/>
              <h3 className="mt-4 font-bold font-heading text-[#273B59]">{v.title}</h3>
              <p className="mt-2 text-sm text-[#475569]">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="ol-section">
      <div className="ol-container text-center">
        <p className="text-xs uppercase tracking-[0.2em] text-[#1088D4] font-semibold">Our Ecosystem</p>
        <h2 className="mt-2 text-3xl sm:text-4xl font-bold font-heading text-[#273B59]">Partnered with 16+ leading lenders</h2>
        <p className="mt-3 text-[#475569] max-w-2xl mx-auto">HDFC, ICICI, Axis, Kotak, IDFC FIRST, IndusInd, Bandhan, Yes Bank, Bajaj Finance, Aditya Birla Finance, Tata Capital, Poonawalla Fincorp, Piramal Finance, Axis Finance, InCred Finance, SMFG India Credit.</p>
        <Link to="/contact"><Button className="mt-7 ol-btn-primary">Get in Touch</Button></Link>
      </div>
    </section>
  </div>
);

export const ContactPage = () => {
  const [form, setForm] = useState({ full_name: "", email: "", mobile: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact(form);
      setSuccess(true);
      toast.success("Message sent! We'll respond within 24 hours.");
    } catch { toast.error("Could not send. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="contact-page" className="ol-container py-10">
      <Breadcrumbs items={[{ label: "Contact Us" }]}/>
      <div className="mt-6 grid lg:grid-cols-2 gap-10">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold font-heading text-[#273B59]">Get in touch.</h1>
          <p className="mt-4 text-[#475569] max-w-md">Have a question about loans, eligibility, or your application? Our team typically responds within 4 hours during business days.</p>
          <div className="mt-8 space-y-5">
            <Info icon={Phone} label="Phone" value="+91 93702 51358" href="tel:+919370251358"/>
            <Info icon={Mail} label="Email" value="support@oneloan.in" href="mailto:support@oneloan.in"/>
            <Info icon={MapPin} label="Office" value="Vasai, India"/>
          </div>
          <div className="mt-8 rounded-2xl overflow-hidden border border-[#E2E8F0]">
            <iframe title="OneLoan Vasai" src="https://www.openstreetmap.org/export/embed.html?bbox=72.78%2C19.32%2C72.88%2C19.42&layer=mapnik&marker=19.37%2C72.83" className="w-full h-64" loading="lazy"/>
          </div>
        </div>
        <div>
          {success ? (
            <div className="bg-[#EAF5FD] border border-[#1088D4]/30 rounded-2xl p-8 text-center">
              <CheckCircle2 className="mx-auto text-[#16A34A]" size={48}/>
              <h3 className="mt-4 text-xl font-bold text-[#273B59] font-heading">Message received</h3>
              <p className="mt-2 text-sm text-[#475569]">Thanks, {form.full_name}. We'll get back to you at {form.email} shortly.</p>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={onSubmit} className="bg-white border border-[#E2E8F0] rounded-2xl p-6 sm:p-8">
              <h3 className="text-xl font-bold font-heading text-[#273B59]">Send a message</h3>
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                <Input data-testid="contact-name" required placeholder="Full name" value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})}/>
                <Input data-testid="contact-email" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}/>
                <Input data-testid="contact-mobile" placeholder="Mobile (optional)" value={form.mobile} onChange={(e) => setForm({...form, mobile: e.target.value})}/>
                <Input data-testid="contact-subject" placeholder="Subject" value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})}/>
                <Textarea data-testid="contact-message" required placeholder="Your message" className="sm:col-span-2 min-h-32" value={form.message} onChange={(e) => setForm({...form, message: e.target.value})}/>
              </div>
              <Button data-testid="contact-submit" disabled={loading} className="w-full mt-5 ol-btn-primary h-11">
                {loading && <Loader2 className="animate-spin mr-2" size={14}/>}
                {loading ? "Sending..." : "Send Message"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const Info = ({ icon: Icon, label, value, href }) => {
  const Inner = (
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 rounded-lg bg-[#EAF5FD] flex items-center justify-center"><Icon className="text-[#1088D4]" size={18}/></div>
      <div>
        <div className="text-xs uppercase tracking-wider text-[#94A3B8]">{label}</div>
        <div className="mt-0.5 text-sm font-semibold text-[#273B59]">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} className="block hover:opacity-80">{Inner}</a> : Inner;
};

export const ApplyPage = () => {
  const [params] = useSearchParams();
  const type = params.get("type") || "personal";
  const lender = params.get("lender");
  return (
    <div data-testid="apply-page" className="ol-container py-10">
      <Breadcrumbs items={[{ label: "Apply Now" }]}/>
      <div className="mt-6 grid lg:grid-cols-2 gap-10 items-start">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold font-heading text-[#273B59]">Apply in under <span className="ol-gradient-text">2 minutes</span>.</h1>
          <p className="mt-4 text-[#475569] max-w-md">Fill in your details. We'll match you with the best lender, get you the lowest rate, and walk you through to disbursal.</p>
          <ul className="mt-7 space-y-3">
            {["100% online & paperless","Soft enquiry — no CIBIL impact","Match with 16+ lenders","Disbursal in 24-72 hours","Dedicated advisor for your application"].map(b => (
              <li key={b} className="flex items-center gap-3 text-sm text-[#273B59]"><CheckCircle2 size={16} className="text-[#16A34A]"/>{b}</li>
            ))}
          </ul>
        </div>
        <LeadForm defaultType={type} lender={lender} title="Get matched with the right lender" source={`apply-${lender || type}`}/>
      </div>
    </div>
  );
};

export const StaticPage = ({ title, body }) => (
  <div data-testid={`static-${title.toLowerCase().replace(/\s+/g,'-')}`} className="ol-container py-10">
    <Breadcrumbs items={[{ label: title }]}/>
    <h1 className="mt-4 text-4xl font-bold font-heading text-[#273B59]">{title}</h1>
    <div className="mt-6 max-w-3xl space-y-4 text-[#475569] leading-relaxed">
      {body.split("\n\n").map((p,i) => <p key={i}>{p}</p>)}
    </div>
  </div>
);
